import React, { forwardRef, Ref, useImperativeHandle, useRef } from 'react';
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput as RNTextInput,
  TextInputFocusEventData,
  TextInputProps as RNTextInputProps,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import generateID from '../../utils/generate-id';

type TextInputProps = {
  accessibilityLabel?: string;
  /**
   * Android only
   */
  autoCompleteType?: RNTextInputProps['autoCompleteType'];
  defaultValue?: string;
  editable?: boolean;
  id?: string;
  /**
   * Set the keyboard type to show when editing this TextInput.
   * Some options are available only on Android, and some only in iOS.
   */
  keyboardType?: KeyboardTypeOptions;
  maxLength?: number;
  multiline?: boolean;
  /**
   * Android only
   */
  numberOfLines?: number;
  onBlur?: RNTextInputProps['onBlur'];
  onChange?: RNTextInputProps['onChange'];
  onChangeText?: RNTextInputProps['onChangeText'];
  onFocus?: RNTextInputProps['onFocus'];
  onSubmitEditing?: RNTextInputProps['onSubmitEditing'];
  placeholder?: string;
  /**
   * Obscure the text being entered such as passwords
   */
  secureTextEntry?: boolean;
  /**
   * iOS only
   */
  textContentType?: RNTextInputProps['textContentType'];
  label?: string;
  value: string;
  message?: string;
  /**
   * Indicate the state of the TextInput.
   * For now only Error state is available.
   */
  state?: 'error';
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  inputStyle?: StyleProp<TextStyle>;
  messageStyle?: StyleProp<TextStyle>;
};

const TextInput = forwardRef(
  (props: TextInputProps, ref: Ref<Partial<RNTextInput>>) => {
    const isError = props.state === 'error';
    const isEditable = props.editable === undefined || !!props.editable;

    // Generate IDs for the TextInput sub-components
    const textInputID = generateID(props.id);
    const labelID = generateID(props.id && `${props.id}-label`);
    const messageID = generateID(props.id && `${props.id}-message`);

    // Custom handles for the RNTextInput events
    const textInputRef = useRef<RNTextInput>(null);

    useImperativeHandle(ref, () => textInputRef.current || {});

    const onFocus = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      props.onFocus?.(event);

      if (!isError && isEditable) {
        textInputRef.current?.setNativeProps({ style: style.inputFocused });
      }
    };

    const onBlur = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      props.onBlur?.(event);

      if (!isError) {
        textInputRef.current?.setNativeProps({ style: style.input });
      }
    };

    return (
      <View style={props.style}>
        {props.label && (
          <Text
            style={[style.label, props.labelStyle]}
            /** Generate HTML and Test IDs  */
            {...labelID}
            /** Platform-specific props */
            {...Platform.select({
              web: {
                accessibilityRole: 'label',
              },
            })}
            {...(isEditable && {
              onPress: () => textInputRef.current?.focus(),
            })}
          >
            {props.label}
          </Text>
        )}
        <RNTextInput
          ref={textInputRef}
          /** props */
          accessibilityLabel={props.accessibilityLabel}
          autoCompleteType={props.autoCompleteType}
          defaultValue={props.defaultValue}
          editable={props.editable}
          keyboardType={props.keyboardType}
          maxLength={props.maxLength}
          multiline={props.multiline}
          numberOfLines={props.numberOfLines}
          onBlur={onBlur}
          onChange={props.onChange}
          onChangeText={props.onChangeText}
          onFocus={onFocus}
          onSubmitEditing={props.onSubmitEditing}
          placeholder={props.placeholder}
          placeholderTextColor="#A3A3A3"
          secureTextEntry={props.secureTextEntry}
          textContentType={props.textContentType}
          value={props.value}
          /** style */
          style={[style.input, isError && style.inputError, props.inputStyle]}
          /** Generate HTML and Test IDs  */
          {...textInputID}
          /** Platform-specific props */
          {...Platform.select({
            web: {
              // TODO: REQUIRES REACT 17 TO WORK, SO PLEASE UPGRADE
              accessibilityLabelledBy: labelID.nativeID,
              accessibilityDescribedBy: props.message
                ? messageID.nativeID
                : undefined,
              disabled: !isEditable,
            },
          })}
        />
        {!!props.message && (
          <Text
            style={[
              style.message,
              props.state && style[props.state],
              props.messageStyle,
            ]}
            /** Generate HTML and Test IDs  */
            {...messageID}
          >
            {props.message}
          </Text>
        )}
      </View>
    );
  }
);

const style = StyleSheet.create({
  input: {
    borderBottomColor: '#EEF0F2',
    borderBottomWidth: 1,
    fontSize: 16,
    height: 40,
    width: '100%',
    paddingVertical: 8,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  inputError: {
    borderBottomColor: '#CA4444',
  },
  inputFocused: {
    borderBottomColor: '#404042',
  },
  label: {
    color: '#404042',
    fontSize: 12,
    fontWeight: '500',
  },
  message: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: '500',
  },
  error: {
    color: '#CA4444',
  },
});

export default TextInput;
export { TextInputProps };
